<script setup lang="ts">
    import { SavedQueryHeaderInfo } from '@/shared/helpers/api';
    import { FolderOrLeaf, isFolder, isLeaf } from '@/shared/helpers/poorMansFolderStructure';
    import { computed, ref } from 'vue';

    type Props = {
        item: FolderOrLeaf<SavedQueryHeaderInfo>;
        isBold: boolean;
        userId: string | null;
        queryClickCallback: (_: SavedQueryHeaderInfo) => void;
        expandLevels?: number;
    };

    const props = withDefaults(defineProps<Props>(), {
        expandLevels: 0
    });

    const isExpanded = ref<boolean | undefined>(
        isFolder(props.item)
            ? (props.expandLevels > 0 ? true : props.item.isExpanded)
            : undefined
    );

    const ownedPublic = computed(() => {
        return props.userId
            && isLeaf(props.item)
            && props.item.value.isPublic
            && props.item.value.userId === props.userId;
    });

    const ownedShared = computed(() => {
        return props.userId
            && isLeaf(props.item)
            && props.item.value.isShared
            && props.item.value.userId === props.userId;
    });

    const itemClass = computed(() => ({
        'is-clickable': true,
        'has-text-weight-bold': props.isBold
    }));
</script>

<template>
    <li class="ml-5">
        <div class="is-flex is-justify-content-space-between library-node">
            <div
                v-if="isFolder(props.item)"
                :class="itemClass"
                @click="isExpanded = !isExpanded">
                <b-icon
                    :title="isExpanded ? 'Collapse' : 'Expand'"
                    :icon="isExpanded ? 'chevron-down' : 'chevron-right'"
                    size="is-small" />
                <b-icon
                    icon="folder"
                    size="is-small"
                    class="mr-1" />
                {{ props.item.name }}
            </div>
            <div
                v-if="isLeaf(props.item)"
                :class="itemClass"
                @click="props.queryClickCallback(props.item.value)">
                <b-icon
                    icon="table-search"
                    size="is-small" />
                {{ props.item.name }}
                <b-icon
                    v-if="ownedPublic"
                    title="Owned by you"
                    icon="lock-open-variant-outline"
                    size="is-small"
                    class="ml-1" />
                <b-icon
                    v-if="ownedShared"
                    title="Shared by you"
                    icon="share-variant"
                    size="is-small"
                    class="ml-1" />
            </div>
        </div>
        <ul v-if="isFolder(props.item) && isExpanded">
            <saved-query-tree-item
                v-for="(child, idx) in props.item.children"
                :key="idx"
                :item="child"
                :is-bold="false"
                :user-id="props.userId"
                :expand-levels="Math.max(0, props.expandLevels - 1)"
                :query-click-callback="props.queryClickCallback" />
        </ul>
    </li>
</template>
